<template>
  <nd-dialog
    class="nd-sign-auth"
    v-model="visible"
    :footer="false"
    closeBtn
    @close="onClose"
  >
    <div class="sign-auth-content">
      <div class="title">{{ title }}</div>

      <div class="tip">{{ tip }}</div>

      <!-- 手机号 -->
      <div class="form-item">
        <nd-input
          ref="mobileInput"
          v-model="mobile"
          type="pure-number"
          maxLength="11"
          placeholder="请输入手机号"
          :readonly="countDown > 0"
          :clearBtn="false"
        ></nd-input>
      </div>

      <!-- 验证码 -->
      <div class="form-item code-item">
        <nd-input
          ref="codeInput"
          v-model="code"
          type="pure-number"
          maxLength="6"
          placeholder="请输入验证码"
          :clearBtn="false"
        ></nd-input>
        <div
          class="send-code"
          :class="{ disabled: !canSend || sending }"
          @click="sendCode"
        >{{ sendBtnText }}</div>
      </div>

      <p class="error-tip error" v-if="errorTip">{{ errorTip }}</p>

      <nd-button
        class="confirm-btn small"
        :disabled="!canConfirm || confirming"
        @click="confirm"
      >确认</nd-button>
    </div>
  </nd-dialog>
</template>
<script>
import { mapActions } from 'vuex'

// 兼容两套响应码约定：全站 000000 / 签章文档 200
function isSuccess (res) {
  return !!res && (res.success === true || res.code === '000000' || res.code === '200')
}

const MOBILE_RE = /^1\d{10}$/
const CODE_RE = /^\d{6}$/

export default {
  name: 'nd-sign-auth',

  props: {
    title: {
      type: String,
      default: '签章授权'
    },
    tip: {
      type: String,
      default: '为保障您的账户安全，请完成签章授权验证'
    }
  },

  data () {
    return {
      visible: false,
      mobile: '',
      code: '',
      countDown: 0,
      transactionId: '',
      errorTip: '',
      sending: false,
      confirming: false,
      done: false,
      duration: 60,
      timer: null,
      extra: {},
      authType: '',
      resolve: null,
      reject: null
    }
  },

  computed: {
    canSend () {
      return this.countDown === 0 && MOBILE_RE.test(this.mobile)
    },
    sendBtnText () {
      if (this.sending) return '发送中'
      if (this.countDown > 0) return `${this.countDown}s`
      return this.transactionId ? '重新获取' : '获取验证码'
    },
    canConfirm () {
      return CODE_RE.test(this.code) && !!this.transactionId
    }
  },

  methods: {
    ...mapActions('signAuth', [
      'checkUserSignAuth',
      'sendSignAuth',
      'confirmSignAuth'
    ]),

    // 函数式调用入口，由 nd-sign-auth/index.js 触发
    start (options = {}) {
      this.mobile = (options.mobile || '').toString()
      this.authType = options.type || ''
      this.extra = {
        userName: options.userName,
        idCard: options.idCard,
        bankCardNo: options.bankCardNo
      }
      this.resetState()
      this.done = false

      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
        this.checkAndOpen()
      })
    },

    resetState () {
      this.code = ''
      this.errorTip = ''
      this.transactionId = ''
      this.countDown = 0
      this.sending = false
      this.confirming = false
      clearInterval(this.timer)
      this.timer = null
    },

    async checkAndOpen () {
      let res
      try {
        res = await this.checkUserSignAuth()
      } catch (e) {
        this.$message('网络异常，请稍后重试')
        return this.fail(e)
      }
      if (!isSuccess(res)) {
        // 业务错误已由 axios 拦截器统一 toast，这里只中止流程
        return this.fail(new Error((res && res.msg) || 'check failed'))
      }
      // 已有授权，无需弹窗，直接通过
      if (res.data && res.data.needAuth === 'N') {
        return this.succeed()
      }
      // 需要授权 → 弹窗
      this.visible = true
      this.$nextTick(() => {
        this.$refs.mobileInput && this.$refs.mobileInput.focus()
      })
    },

    async sendCode () {
      if (!this.canSend || this.sending) return
      if (!MOBILE_RE.test(this.mobile)) {
        this.errorTip = '请输入正确的手机号'
        return
      }
      this.errorTip = ''
      this.sending = true
      let res
      try {
        const params = { mobile: this.mobile }
        if (this.authType) params.type = this.authType
        res = await this.sendSignAuth(params)
      } catch (e) {
        this.sending = false
        this.errorTip = '验证码发送失败，请重试'
        return
      }
      this.sending = false
      if (isSuccess(res) && res.data && res.data.transactionId) {
        this.transactionId = res.data.transactionId
        this.startCount()
        this.$message('验证码已发送')
        this.$nextTick(() => {
          this.$refs.codeInput && this.$refs.codeInput.focus()
        })
      }
      // 发送失败由 axios 拦截器统一 toast，按钮恢复后用户可重试
    },

    async confirm () {
      if (!this.canConfirm || this.confirming) return
      this.errorTip = ''
      this.confirming = true
      const params = {
        transactionId: this.transactionId,
        mobile: this.mobile,
        smsCode: this.code
      }
      // 可选参数：绑卡等场景按需传入
      if (this.extra.userName) params.userName = this.extra.userName
      if (this.extra.idCard) params.idCard = this.extra.idCard
      if (this.extra.bankCardNo) params.bankCardNo = this.extra.bankCardNo

      let res
      try {
        res = await this.confirmSignAuth(params)
      } catch (e) {
        this.onConfirmFailed('验证失败，请重新获取验证码')
        return
      }
      this.confirming = false
      if (isSuccess(res)) {
        this.$message('签章授权成功')
        this.visible = false
        this.succeed()
      } else {
        // 文档：同一 transactionId 只能验证一次，失败需重新获取
        this.onConfirmFailed((res && res.msg) || '验证失败，请重新获取验证码')
      }
    },

    onConfirmFailed (msg) {
      this.confirming = false
      this.transactionId = ''
      this.code = ''
      this.countDown = 0
      clearInterval(this.timer)
      this.errorTip = msg
    },

    startCount () {
      this.countDown = this.duration
      clearInterval(this.timer)
      this.timer = setInterval(() => {
        this.countDown -= 1
        if (this.countDown <= 0) {
          clearInterval(this.timer)
          this.timer = null
          this.countDown = 0
        }
      }, 1000)
    },

    // 用户主动关闭弹窗
    onClose () {
      if (this.done) return
      clearInterval(this.timer)
      this.fail(new Error('cancel'))
    },

    succeed () {
      if (this.done) return
      this.done = true
      this.resolve && this.resolve({ mobile: this.mobile })
    },

    fail (err) {
      if (this.done) return
      this.done = true
      this.reject && this.reject(err)
    }
  },

  destroyed () {
    clearInterval(this.timer)
    this.timer = null
  }
}
</script>
<style lang="less" scoped>
@import "../../assets/less/mixin.less";

.nd-sign-auth {
  .sign-auth-content {
    padding: 0 45px;
    padding-bottom: 40px;
    position: relative;

    .title {
      font-size: 42px;
      height: 72px;
      line-height: 72px;
      font-family: PingFangSC-Semibold, PingFang SC;
      font-weight: 600;
    }

    .tip {
      font-size: 28px;
      line-height: 40px;
      margin-top: 30px;
      font-family: PingFangSC-Regular, PingFang SC;
      font-weight: 400;
      color: @fc3;
    }

    .form-item {
      height: 110px;
      line-height: 110px;
      padding: 0 35px;
      margin-top: 30px;
      background-color: #fafafa;
      display: flex;
      align-items: center;

      .nd-input {
        font-size: 36px;
        height: 50px;
        line-height: 50px;
        flex: 1;
        font-family: PingFangSC-Regular, PingFang SC;
        font-weight: 400;

        input {
          font-size: 36px;
          font-family: PingFangSC-Regular, PingFang SC;
          font-weight: 400;
        }
      }

      &.code-item {
        .send-code {
          font-size: 26px;
          width: 160px;
          flex-shrink: 0;
          text-align: center;
          font-family: PingFangSC-Regular, PingFang SC;
          font-weight: 400;
          color: @theme;

          &.disabled {
            color: @fc4;
          }
        }
      }
    }

    .error-tip {
      font-size: 24px;
      line-height: 33px;
      margin-top: 16px;
      text-align: center;
      font-family: PingFangSC-Regular, PingFang SC;
      font-weight: 400;
    }

    .confirm-btn {
      width: 320px;
      height: 80px;
      line-height: 80px;
      margin: 60px auto 0;
      display: block;
    }
  }
}
</style>
